import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Box, CircularProgress, Button, Backdrop, Stack, Alert,
  Card, CardContent, Divider, useTheme, Fab
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import dayjs, { Dayjs } from 'dayjs';

import ServiceSelector from './ServiceSelector';
import DateSelector from './DateSelector';
import TimeSelector from './TimeSelector';
import ContactForm from './ContactForm';

import { bookAppointment, Service, BookingRequest, getBookedSlots, BookedSlot, calculateAvailableTimeSlots, getAvailableDates, WorkDay, NotesField } from '../services/bookingService';
import { isBackgroundDark } from '../services/themeBuilder';

interface BusinessBookSectionProps {
  businessId: string;
  businessName: string;
  services: Service[];
  workDays: number[];
  workSchedule: WorkDay[];
  notesField?: NotesField | null;
}


export const BusinessBookSection: React.FC<BusinessBookSectionProps> = ({
  businessId, businessName, services, workDays, workSchedule, notesField
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDarkBg = isBackgroundDark(theme.palette.background.default);

  // Refs for smooth scrolling to current step
  const servicesRef = useRef<HTMLDivElement | null>(null);
  const dateRef = useRef<HTMLDivElement | null>(null);
  const timeRef = useRef<HTMLDivElement | null>(null);
  const contactRef = useRef<HTMLDivElement | null>(null);
  const bookingSectionRef = useRef<HTMLDivElement | null>(null);

  // Track if user is in booking section
  const [isInBookingSection, setIsInBookingSection] = useState(false);

  // Intersection observer to detect when user is in booking section
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInBookingSection(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (bookingSectionRef.current) {
      observer.observe(bookingSectionRef.current);
    }

    return () => observer.disconnect();
  }, []);


  // Selections
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const selectedIds = useMemo(() => selectedServices.map(s => s.id), [selectedServices]);
  const totalDuration = useMemo(() => selectedServices.reduce((sum, s) => sum + (s.duration || 0), 0), [selectedServices]);
  const totalPrice = useMemo(() => selectedServices.reduce((sum, s) => sum + (s.serviceTypePrice || 0), 0), [selectedServices]);

  // Date/Time
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [timeSlotsLoading, setTimeSlotsLoading] = useState(false);
  
  // Booked slots and available dates
  const [bookedSlots, setBookedSlots] = useState<BookedSlot[]>([]);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [bookedSlotsLoading, setBookedSlotsLoading] = useState(false);

  // Contact
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');

  // Scroll to next section helper
  const scrollToNextSection = useCallback(() => {
    if (selectedServices.length > 0 && !selectedDate) {
      dateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (selectedDate && !selectedTime) {
      timeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (selectedTime && (!customerName || !customerPhone || !customerEmail || !customerAddress || (notesField?.required && !customerNotes.trim()))) {
      contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedServices.length, selectedDate, selectedTime, customerName, customerPhone, customerEmail, customerAddress, customerNotes, notesField]);


  // UX
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [validateForm, setValidateForm] = useState(false);

  const timeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const estimatedEnd = useMemo(() => {
    if (!selectedDate || !selectedTime || !totalDuration) return null;
    return dayjs(`${selectedDate.format('YYYY-MM-DD')} ${selectedTime}`, 'YYYY-MM-DD h:mm A')
      .add(totalDuration, 'minute')
      .format('h:mm A');
  }, [selectedDate, selectedTime, totalDuration]);

  // Fetch booked slots when services are selected (90 days range)
  useEffect(() => {
    const run = async () => {
      if (selectedServices.length === 0) {
        setBookedSlots([]);
        setAvailableDates([]);
        return;
      }
      
      setBookedSlotsLoading(true);
      setErrorMessage(null);
      try {
        const today = dayjs().format('YYYY-MM-DD');
        const endDate = dayjs().add(90, 'day').format('YYYY-MM-DD');
        
        const slots = await getBookedSlots(businessId, today, endDate);
        setBookedSlots(slots);
        
        // Calculate available dates
        const available = getAvailableDates(slots, workSchedule, totalDuration, today, endDate);
        setAvailableDates(available);
      } catch (err) {
        console.error('Error fetching booked slots:', err);
        setBookedSlots([]);
        setAvailableDates([]);
        setErrorMessage('Unable to load availability. Please try again.');
      } finally {
        setBookedSlotsLoading(false);
      }
    };
    run();
  }, [selectedServices, businessId, totalDuration, workSchedule]);
  
  // Calculate time slots for selected date using booked slots
  useEffect(() => {
    if (!selectedDate || selectedServices.length === 0) {
      setTimeSlots([]);
      return;
    }
    
    const dateString = selectedDate.format('YYYY-MM-DD');
    const slots = calculateAvailableTimeSlots(dateString, bookedSlots, workSchedule, totalDuration);
    setTimeSlots(slots);
  }, [selectedDate, bookedSlots, workSchedule, totalDuration, selectedServices]);

  // Toggle a service
  const onToggle = useCallback((id: string) => {
    setSelectedServices(prev => {
      const exists = prev.some(s => s.id === id);
      return exists ? prev.filter(s => s.id !== id)
                    : [...prev, services.find(s => s.id === id)!];
    });
    // reset time if services change
    setSelectedTime('');
  }, [services]);

  // Booking validity
  const canBook = useMemo(() => {
    const basicFieldsValid = selectedServices.length > 0 && !!selectedDate && !!selectedTime && 
           !!customerName && !!customerPhone && !!customerEmail && !!customerAddress;
    
    // If notes field is required, check that notes are provided
    const notesValid = !notesField || !notesField.required || !!customerNotes.trim();
    
    return basicFieldsValid && notesValid;
  }, [selectedServices.length, selectedDate, selectedTime, customerName, customerPhone, customerEmail, customerAddress, customerNotes, notesField]);




  const handleBookingSubmit = async () => {
    if (!selectedDate || !selectedTime || selectedServices.length === 0) return;
    
    // Trigger form validation
    setValidateForm(true);
    
    // Check if form is valid before proceeding
    const basicFieldsValid = selectedServices.length > 0 && !!selectedDate && !!selectedTime && 
           !!customerName && !!customerPhone && !!customerEmail && !!customerAddress;
    const notesValid = !notesField || !notesField.required || !!customerNotes.trim();
    
    if (!basicFieldsValid || !notesValid) {
      setBookingInProgress(false);
      return;
    }
    
    setBookingInProgress(true);
    setErrorMessage(null);
    try {
      const bookingRequest: BookingRequest = {
        businessId,
        services: selectedServices,
        date: selectedDate.format('YYYY-MM-DD'),
        time: selectedTime,
        customerName,
        customerEmail,
        customerPhone,
        customerLocation: customerAddress,
        notes: customerNotes,
        timeZone
      };
      const result = await bookAppointment(bookingRequest);
      navigate('/confirmation', {
        state: {
          bookingId: result.bookingId,
          confirmationNumber: result.confirmationNumber,
          businessName,
          services: selectedServices,
          date: selectedDate.format('MMMM D, YYYY'),
          time: selectedTime,
          customerName,
          customerEmail,
          customerPhone
        }
      });
    } catch (error: any) {
      console.error('Booking error:', error);
      if (error?.message === 'TIME_SLOT_UNAVAILABLE') {
        setErrorMessage('The selected time slot is no longer available. Please choose a different time.');
        setTimeSlots(prev => prev.filter(slot => slot !== selectedTime));
        setSelectedTime('');
      } else {
        setErrorMessage('Failed to book appointment. Please try again.');
      }
    } finally {
      setBookingInProgress(false);
    }
  };

  return (
    <>
      <Box id="book" ref={bookingSectionRef} sx={{ scrollMarginTop: 80 }}>
        <Box maxWidth="1000px" mx="auto" px={{ xs: 2, sm: 3 }} py={{ xs: 3, sm: 4 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 800, 
              mb: 2,
              color: isDarkBg ? '#FFFFFF' : 'text.primary'
            }}
          >
            Book an appointment
          </Typography>

          {/* Step 1: Services */}
          <Box sx={{ position: 'relative' }}>
            <Card ref={servicesRef} variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>1. Choose services</Typography>
                <ServiceSelector services={services} selected={selectedIds} onToggle={onToggle} />
              </CardContent>
            </Card>
          </Box>

          {/* Date Selection */}
          {selectedServices.length > 0 && (
            <Box sx={{ position: 'relative' }}>
              <Card ref={dateRef} variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>2. Pick a date</Typography>
                  {bookedSlotsLoading ? (
                    <Box display="flex" justifyContent="center" mt={2}><CircularProgress size={24} /></Box>
                  ) : (
                    <>
                      <DateSelector
                        selectedDate={selectedDate}
                        onChange={(date) => { setSelectedDate(date); setSelectedTime(''); }}
                        workDays={workDays}
                        availableDates={availableDates}
                      />
                      {!selectedDate && availableDates.length === 0 && !bookedSlotsLoading && (
                        <Typography variant="body2" color="error" mt={1}>
                          No available dates in the next 90 days.
                        </Typography>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Time Selection */}
          {selectedDate && selectedServices.length > 0 && (
            <Box sx={{ position: 'relative' }}>
              <Card ref={timeRef} variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>3. Pick a time</Typography>
                  {timeSlots.length === 0 ? (
                    <Typography variant="body2" color="error" mt={1}>
                      There are no available time slots for the selected day.
                    </Typography>
                  ) : (
                    <TimeSelector timeSlots={timeSlots} selectedTime={selectedTime} onSelect={setSelectedTime} />
                  )}
                  {selectedTime && (
                    <Typography variant="body2" mt={1} color="text.secondary">
                      Estimated end: <strong>{estimatedEnd}</strong> • {totalDuration} min
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Contact Information */}
          {selectedDate && selectedServices.length > 0 && selectedTime && (
            <Card ref={contactRef} variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>4. Your info</Typography>
                <ContactForm
                  name={customerName}
                  phone={customerPhone}
                  email={customerEmail}
                  address={customerAddress}
                  appointmentNotes={customerNotes}
                  notesField={notesField}
                  validateAll={validateForm}
                  onChange={(field, value) => {
                    if (field === 'name') setCustomerName(value);
                    if (field === 'phone') setCustomerPhone(value);
                    if (field === 'email') setCustomerEmail(value);
                    if (field === 'address') setCustomerAddress(value);
                    if (field === 'appointmentNotes') setCustomerNotes(value);
                  }}
                />
              </CardContent>
            </Card>
          )}

          {/* Errors */}
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}

          {/* Booking summary bar */}
          <Card
            variant="outlined"
            sx={{
              borderRadius: 3,
              // position: 'sticky',
              bottom: 12,
              zIndex: theme.zIndex.appBar - 1,
              backdropFilter: 'blur(4px)'
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedServices.length} service(s)
                  </Typography>
                  <Divider orientation="vertical" flexItem />
                  <Typography variant="body2" color="text.secondary">
                    {totalDuration} min
                  </Typography>
                  <Divider orientation="vertical" flexItem />
                  <Typography variant="body2" color="text.secondary">
                    {selectedDate ? selectedDate.format('MMM D') : '--'} {selectedTime || ''}
                  </Typography>
                  <Divider orientation="vertical" flexItem />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {totalPrice.toLocaleString(undefined, { style: 'currency', currency: 'USD' })}
                  </Typography>
                </Stack>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBookingSubmit}
                  disabled={!canBook || bookingInProgress}
                  sx={{ minWidth: 140 }}
                >
                  {bookingInProgress ? 'Booking…' : 'Book Appointment'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Fixed scroll indicator */}
      {isInBookingSection && ((selectedServices.length > 0 && !selectedDate) || 
        (selectedDate && !selectedTime) || 
        (selectedTime && (!customerName || !customerPhone || !customerEmail || !customerAddress || (notesField?.required && !customerNotes.trim())))) && (
        <Fab
          size="medium"
          color="primary"
          onClick={() => {
            if (selectedServices.length > 0 && !selectedDate) {
              dateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else if (selectedDate && !selectedTime) {
              timeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else if (selectedTime && (!customerName || !customerPhone || !customerEmail || !customerAddress || (notesField?.required && !customerNotes.trim()))) {
              contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }}
          sx={{
            position: 'fixed',
            bottom: 120,
            right: 24,
            zIndex: theme.zIndex.modal + 2,
            animation: 'bounce 2s infinite'
          }}
        >
          <KeyboardArrowDownIcon />
        </Fab>
      )}

      {/* CSS for bounce animation */}
      <style>
        {`
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% {
              transform: translateY(0);
            }
            40% {
              transform: translateY(-10px);
            }
            60% {
              transform: translateY(-5px);
            }
          }
        `}
      </style>

      {/* Booking overlay */}
      <Backdrop
        open={bookingInProgress}
        sx={{ zIndex: (t) => t.zIndex.modal + 1, color: '#fff', backdropFilter: 'blur(2px)' }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="inherit" />
          <Typography variant="body1">Booking your appointment…</Typography>
        </Stack>
      </Backdrop>
    </>
  );
};
